import { ColumnDef } from '@tanstack/react-table'
import AssetTableAssetViz from 'components/AssetTableAssetViz'
import { TagCell } from 'components/table/cells/TagCell'
import { TextCell } from 'components/table/cells/TextCell'
import { ThumbnailCell } from 'components/table/cells/ThumbnailCell'
import { Thumbnail } from 'components/table/utility/Thumbnail'
import { AssetImage } from 'features/assetDatabase/components/assetImage/AssetImage'
import { AssetData } from 'features/assetDatabase/types/assetTableTypes'
import React, { useMemo } from 'react'
import { InputType } from 'types'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { convertDateToString } from './AssetTableHelpers'

const AssetTableColumns = (siteID: string): ColumnDef<AssetData>[] => {
  return useMemo(() => {
    const defaultColumns: ColumnDef<AssetData>[] = [
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Asset',
        filterFn: 'includesString',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => {
          return (
            <ThumbnailCell
              data-testid={`asset-${row.original.id}`}
              thumbnailString={row.getValue('name')}
              to='/assets/$siteID/$unitID'
              from='/assets/$siteID'
              params={{ siteID, unitID: row.original.id }}
              search={{
                view: 'details',
              }}>
              <Thumbnail>
                <AssetImage
                  primaryImageID={row.original.unitPrimaryImage}
                  assetModelType={row.original.assetModelType}
                />
              </Thumbnail>
            </ThumbnailCell>
          )
        },
      },
      {
        accessorKey: 'lastInspectionDate',
        id: 'lastInspectionDate',
        header: 'Last Inspection',
        sortingFn: 'datetime',
        sortUndefined: 'last',
        filterFn: 'dateRangeFilter',
        meta: {
          inputType: InputType.Date,
        },
        cell: ({ row }) => (
          <ThumbnailCell
            thumbnailString={convertDateToString(
              row.getValue('lastInspectionDate')
            )}
            to={
              convertDateToString(row.original.lastInspectionDate)
                ? '/details/$entryID'
                : '/assets/$siteID/$unitID'
            }
            from='/assets/$siteID'
            params={{
              entryID: row.original.lastInspectionSlug,
              siteID,
              unitID: row.original.id,
            }}
            search={{ view: 'details' }}
            hoverable={Boolean(row.original.lastInspectionDate)}
            emptyText={'Not yet inspected'}>
            {
              <Thumbnail>
                <AssetTableAssetViz
                  cardProps={row.original.cardProps}
                  unitID={row.original.id}
                  siteID={siteID}
                />
              </Thumbnail>
            }
          </ThumbnailCell>
        ),
      },
      {
        accessorKey: 'lastUpdated',
        id: 'lastUpdated',
        header: 'Last Updated',
        sortingFn: 'datetime',
        sortUndefined: 'last',
        filterFn: 'dateRangeFilter',
        meta: {
          inputType: InputType.Date,
        },
        cell: ({ row }) => (
          <TextCell text={convertDateToString(row.getValue('lastUpdated'))} />
        ),
      },
      {
        accessorKey: 'unitComponents',
        id: 'unitComponents',
        header: 'Components',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: {
          inputType: InputType.Number,
        },
        cell: ({ row }) => (
          <TagCell
            text={(row.getValue('unitComponents') as number).toString()}
          />
        ),
      },
      {
        accessorKey: 'area',
        id: 'area',
        header: 'Area',
        sortingFn: (rowA, rowB) => {
          const valueA =
            rowA.original.area === undefined ? '' : rowA.original.area
          const valueB =
            rowB.original.area === undefined ? '' : rowB.original.area
          return compareAlphanumeric(valueA, valueB)
        },
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Combobox,
        },
        cell: ({ row }) => (
          <TextCell
            text={row.getValue('area')}
            subtitle={row.original.areaDescription ?? ''}
          />
        ),
      },
      {
        accessorKey: 'areaDescription',
        id: 'areaDescription',
        header: 'Area Description',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => <TextCell text={row.getValue('areaDescription')} />,
      },
      {
        accessorKey: 'erpId',
        id: 'erpId',
        header: 'ERP ID',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => <TextCell text={row.getValue('erpId')} />,
      },
    ]

    return defaultColumns
  }, [siteID])
}

export default AssetTableColumns
